import {
    MAX_TOOL_SLOT_ACTION_DESCRIPTION,
    MAX_TOOL_SLOT_ACTIONS,
    TOOL_SLOT_ACTION,
    type ToolSlot,
} from '@zerohuman/shared'

/**
 * #957: the rules a task's slot actions are checked by on the task page before a save, the same ones the API applies
 * (`tool_slot_action_*`), and the words for the API's own refusals of them. Every action name comes from the task
 * being edited: none is written here.
 */

/** Why an action name cannot stand in its slot (`others` are the slot's other names), or null when it can. */
export function actionNameIssue(name: string, others: readonly string[]): string | null {
    const trimmed = name.trim()
    if (!trimmed) return 'Give the action a name.'
    if (!TOOL_SLOT_ACTION.test(trimmed)) {
        return 'An action name is lowercase words of letters, digits and _, joined by dots.'
    }
    if (others.some(other => other.trim() === trimmed)) return 'This slot already has an action with that name.'
    return null
}

/** Why an action's description cannot be saved, or null when it can. */
export function actionDescriptionIssue(description: string | null | undefined): string | null {
    return (description ?? '').trim().length > MAX_TOOL_SLOT_ACTION_DESCRIPTION
        ? `Keep the description to ${MAX_TOOL_SLOT_ACTION_DESCRIPTION} characters.`
        : null
}

/** Why one more action cannot be added to a slot holding `count`, or null when it can. */
export function actionCountIssue(count: number): string | null {
    return count >= MAX_TOOL_SLOT_ACTIONS ? `A slot declares at most ${MAX_TOOL_SLOT_ACTIONS} actions.` : null
}

/** Whether any slot's actions break a rule, so the page sends nothing and the rows say why. */
export function toolSlotActionsBlocked(slots: readonly ToolSlot[]): boolean {
    return slots.some(slot => {
        const actions = slot.actions ?? []
        if (actions.length > MAX_TOOL_SLOT_ACTIONS) return true
        return actions.some(
            (action, index) =>
                actionNameIssue(
                    action.name,
                    actions.filter((_other, at) => at !== index).map(other => other.name),
                ) !== null || actionDescriptionIssue(action.description) !== null,
        )
    })
}

/** A save the API refused over a slot's actions: the slot, the action it named (none for the whole list), the words. */
export type SlotActionRefusal = { key: string; action: string | null; message: string }

const ACTION_REFUSALS: ReadonlyMap<string, string> = new Map([
    ['tool_slot_action_invalid', 'Not an action name: use lowercase words of letters, digits and _, joined by dots.'],
    ['tool_slot_action_duplicate', 'This slot already has an action with that name.'],
    [
        'tool_slot_action_description_invalid',
        `Keep the description to ${MAX_TOOL_SLOT_ACTION_DESCRIPTION} characters of plain text.`,
    ],
])

/**
 * The refusal a save's error names (`tool_slot_action_invalid:<key>.<name>`, `tool_slot_actions_invalid:<key>`) in
 * words, with where it goes, or null for any other error. The code itself is never part of the words.
 */
export function slotActionRefusal(code: string): SlotActionRefusal | null {
    const colon = code.indexOf(':')
    if (colon < 0) return null
    const reason = code.slice(0, colon)
    const target = code.slice(colon + 1)
    if (reason === 'tool_slot_actions_invalid' && target) {
        return {
            key: target,
            action: null,
            message: `This slot's actions could not be saved: at most ${MAX_TOOL_SLOT_ACTIONS}, each a name and an optional description.`,
        }
    }
    const message = ACTION_REFUSALS.get(reason)
    const dot = target.indexOf('.')
    if (!message || dot <= 0) return null
    return { key: target.slice(0, dot), action: target.slice(dot + 1), message }
}
